import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalRowSelectionAllCheckbox } from 'src/common/components/ConfigurableTable/Components/Rows/ExternalRowSelection/ExternalRowSelectionAllCheckbox';
import { ColumnConfiguration } from 'src/common/components/ConfigurableTable/models/configurableTable';
import { getHuminizeDurationInSecFunction } from 'src/i18n/dateLocale';
import { toLocaleBrowserSettings } from 'src/i18n/numberLocale';

const humanize = getHuminizeDurationInSecFunction(['m', 's'], true);

export const useTariffSummaryColumns = (isPreviousMonthFallback = false) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const baseColumns: ColumnConfiguration[] = [
      {
        field: 'select-action',
        type: 'actions',
        width: 50,
        resizable: false,
        sortable: false,
        headerName: '',
        HeaderComponent: ExternalRowSelectionAllCheckbox,
        headerClassName: 'MuiDataGrid-columnHeaderCheckbox',
      },
      {
        field: 'tariffDescription',
        headerName: t('tariffSummary.tableHeaders.tariffDescription'),
        resizable: false,
        sortable: false,
        pinnable: false,
        width: 300,
        minWidth: 220,
        maxWidth: 400,
      },
      {
        field: 'bundleProductCode',
        headerName: t('tariffSummary.tableHeaders.tariffCode'),
        resizable: false,
        sortable: true,
        pinnable: false,
        width: 140,
        minWidth: 70,
        maxWidth: 300,
      },
      {
        field: 'alertConfiguration',
        headerName: t('tariffSummary.tableHeaders.alertConfiguration'),
        resizable: false,
        sortable: false,
        pinnable: false,
        width: 80,
        minWidth: 80,
        maxWidth: 80,
      },
      {
        field: 'activeCount',
        headerName: t('tariffSummary.tableHeaders.active'),
        resizable: false,
        sortable: true,
        pinnable: false,
        align: 'left',
        headerAlign: 'left',
        width: isPreviousMonthFallback ? 400 : 100,
        minWidth: 70,
        maxWidth: isPreviousMonthFallback ? 600 : 300,
        valueGetter: (value) =>
          value == null
            ? t('tariffSummary.table.noDataForBillingMonth')
            : toLocaleBrowserSettings(value),
      },
    ];

    if (isPreviousMonthFallback) {
      return baseColumns;
    }

    const config: ColumnConfiguration[] = [
      ...baseColumns,
      {
        field: 'usedData',
        headerName: t('tariffSummary.tableHeaders.data'),
        resizable: false,
        sortable: true,
        pinnable: false,
        width: 200,
        minWidth: 200,
        maxWidth: 300,
        headerAlign: 'right',
        align: 'right',
        subField: {
          field: 'forecastData',
          headerName: t('tariffSummary.tableHeaders.forecastData'),
          sortable: true,
        },
      },
      {
        field: 'allowanceData',
        headerName: t('tariffSummary.tableHeaders.poolDataAllowance'),
        resizable: false,
        sortable: true,
        pinnable: false,
        width: 180,
        minWidth: 70,
        maxWidth: 250,
        valueGetter: (value) =>
          value == null ? t('tariffSummary.table.noData') : toLocaleBrowserSettings(value),
      },
      {
        field: 'usedSeconds',
        headerName: t('tariffSummary.tableHeaders.voice'),
        resizable: false,
        sortable: true,
        pinnable: false,
        width: 200,
        minWidth: 200,
        maxWidth: 300,
        headerAlign: 'right',
        align: 'right',
        subField: {
          field: 'forecastSeconds',
          headerName: t('tariffSummary.tableHeaders.forecastVoice'),
          sortable: true,
        },
      },
      {
        field: 'allowanceSeconds',
        headerName: t('tariffSummary.tableHeaders.poolVoiceAllowance'),
        resizable: false,
        sortable: true,
        pinnable: false,
        width: 200,
        minWidth: 70,
        maxWidth: 250,
        valueGetter: (value) => (value == null ? t('tariffSummary.table.noData') : humanize(value)),
      },
      {
        field: 'sent',
        headerName: t('tariffSummary.tableHeaders.sms'),
        resizable: false,
        sortable: true,
        pinnable: false,
        width: 200,
        minWidth: 200,
        maxWidth: 250,
        align: 'right',
        headerAlign: 'right',
        subField: {
          field: 'forecastSMS',
          headerName: t('tariffSummary.tableHeaders.forecastSms'),
          sortable: true,
        },
      },
      {
        field: 'allowanceSMS',
        headerName: t('tariffSummary.tableHeaders.poolSMSAllowance'),
        resizable: false,
        sortable: true,
        pinnable: false,
        width: 200,
        minWidth: 70,
        maxWidth: 250,
        valueGetter: (value) =>
          value == null ? t('tariffSummary.table.noData') : toLocaleBrowserSettings(value),
      },
    ];

    return config;
  }, [isPreviousMonthFallback]);
};
